/* eslint-disable no-console */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { invokeTypedService } from '../services';
import { SEARCH_API_ROUTES } from '../constant/URL';
import { getUserData } from '../util/index';
import {
  TypeAheadDropdownItem,
  TypeAheadResponse,
  TypeAheadState,
  RecentSearchApiResponse // Add this type
} from '../interfaces';
import { SearchStorageManager } from '../util/searchStorageManager';
// import * as commonAction from '../../../common/actions';
import { logErrorToSplunk } from '../../../service';

// Define the initial state
const initialState: TypeAheadState = {
  suggestions: {
    regions: [],
    resorts: [],
    locations: [],
    all: [],
  },
  isLoading: false,
  error: null,
  searchQuery: '',
  selectedItem: null,
};

/**
 * Helper function to make recent search API call
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
const makeRecentSearchApiCall = async (): Promise<boolean> => {
  try {
    // UPDATED: Use new storage manager
    const payLoadString = SearchStorageManager.getItem('selectedSuggestion');
    
    let payLoad: Record<string, unknown> | null = null;
    try {
      payLoad = payLoadString ? (typeof payLoadString === 'string' ? JSON.parse(payLoadString) : payLoadString) : null;
    } catch (parseError) {
      console.error('Error parsing selectedSuggestion from new localStorage structure:', parseError);
      payLoad = null;
    }
    
    if (payLoad) {
      const memberId = getUserData().memberId;
      const favoritesResponse = await invokeTypedService<RecentSearchApiResponse>({
        resource: SEARCH_API_ROUTES.RECENT_SEARCH_POST_URL(String(memberId)),
        data: payLoad,
        method: 'POST',
      });
      
      if (favoritesResponse && !!favoritesResponse.status) {
        try {
          // UPDATED: Clear from new storage structure
          SearchStorageManager.removeItem('recentSearchData');
          return true;
        } catch (error) {
          console.error('Error clearing recent search data:', error);
          return false;
        }
      } else {
        console.warn('Recent search API call failed or returned invalid status');
        return false;
      }
    } else {
      console.warn('No valid payload found in new localStorage structure for selectedSuggestion');
      return false;
    }
  } catch (favoritesError) {
    console.error('Error calling recent search API service:', favoritesError);
    return false;
  }
};

// Create an async thunk for fetching typeahead suggestions
export const fetchTypeAheadSuggestions = createAsyncThunk(
  'typeahead/fetchSuggestions',
  async (searchQuery: string, { rejectWithValue }) => {
    try {
      // Skip API call if search query is too short
      if (searchQuery.length < 2) {
        return {
          '@type': 'TypeAheadPanel',
          searchGroup: {
            rciDictValues: [],
            rciValues: [],
            resortValues: [],
            geocodeValues: [],
          },
        } as TypeAheadResponse;
      }

      const serviceUrl = window.weeksWebCloudConfig?.REACT_APP_SERVICE_URL || '/api';
      
      // Build query parameters properly
      const queryParams = new URLSearchParams({
        Ntt: `${searchQuery}*`,
        consumerChannelQ: 'Web',
        memberTypeQ: 'WEEKS',
        operatorIdQ: 'WEB00USER',
        customerIdQ: '045062689', // This should come from user context
      });

      const response = await fetch(
        `${serviceUrl}/searchTypeahead/inventory/en?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Typeahead search failed with status: ${response.status}`);
      }

      const data = await response.json() as TypeAheadResponse;
      
      // ADDED: Make recent search API call after successful typeahead (non-blocking)
      makeRecentSearchApiCall().catch(error => {
        // Log error to Splunk
        try {
              logErrorToSplunk(error, SEARCH_API_ROUTES.RECENT_SEARCH_POST_URL(String(getUserData().memberId)), 'POST', 'favorites-recent-search - recentSearchPostApiCall');
            } catch (logError) {
              console.error('Error logging failed:', logError);
            }
        console.error('Recent search API call failed:', error);
        // Don't fail the main typeahead operation
      });
      
      return data;
    } catch (error) {
      // Log error to Splunk
        try {
              const serviceUrl = window.weeksWebCloudConfig?.REACT_APP_SERVICE_URL || '/api';
              const queryParams = new URLSearchParams({
                Ntt: `${searchQuery}*`,
                consumerChannelQ: 'Web',
                memberTypeQ: 'WEEKS',
                operatorIdQ: 'WEB00USER',
                customerIdQ: '045062689', // This should come from user context
              });
              logErrorToSplunk(error, `${serviceUrl}/searchTypeahead/inventory/en?${queryParams.toString()}`, 'GET', 'Typeahead - fetchSuggestions');
            } catch (logError) {
              console.error('Error logging failed:', logError);
            }
      return rejectWithValue((error as Error).message);
    }
  }
);

// Update saveToRecentSearch thunk
export const saveToRecentSearch = createAsyncThunk(
  'typeahead/saveToRecentSearch',
  async (selectedItem: TypeAheadDropdownItem, { rejectWithValue }) => {
    try {
      // UPDATED: Store in new localStorage structure
      SearchStorageManager.setItem('selectedSuggestion', selectedItem);
      
      const success = await makeRecentSearchApiCall();
      
      if (!success) {
        throw new Error('Failed to save to recent search');
      }
      
      return selectedItem;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Create the typeahead slice
const typeaheadSlice = createSlice({
  name: 'typeahead',
  initialState,
  reducers: {
    setTypeAheadQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },
    clearTypeAheadSuggestions(state) {
      state.suggestions = initialState.suggestions;
    },
    setSelectedItem(state, action: PayloadAction<TypeAheadDropdownItem>) {
      state.selectedItem = action.payload;
      // UPDATED: Store in new localStorage structure
      try {
        SearchStorageManager.setItem('selectedSuggestion', action.payload);
      } catch (error) {
        console.error('Error storing selected suggestion in new localStorage structure:', error);
      }
    },
    clearSelectedItem(state) {
      state.selectedItem = null;
      // state.isLoading = false;
      // state.error = null;
      // UPDATED: Clear from new localStorage structure
      try {
        SearchStorageManager.removeItem('selectedSuggestion');
      } catch (error) {
        console.error('Error clearing selected suggestion from new localStorage structure:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Existing fetchTypeAheadSuggestions cases
      .addCase(fetchTypeAheadSuggestions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTypeAheadSuggestions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null; // clear previous errors on successful fetch
        
        // Process and categorize the suggestions
        const response = action.payload;

        // Extract regions from both rciDictValues and rciValues
        const regions = [
          ...response.searchGroup.rciDictValues.filter(
            item => item.typeName === 'Dimension' && 
                   (item.displayAddLabel.includes('Region') || 
                    item.displayAddLabel.includes('State'))
          ),
          ...response.searchGroup.rciValues.filter(
            item => item.typeName === 'Dimension' && 
                   item.displayAddLabel.includes('Region')
          ),
        ];

        // Get resort suggestions
        const resorts = response.searchGroup.resortValues;

        // Get location suggestions (geocode values)
        const locations = response.searchGroup.geocodeValues;

        // Combine all suggestions
        const all = [
          ...regions, 
          ...resorts, 
          ...locations
        ];

        // Update state with organized suggestions
        state.suggestions = {
          regions,
          resorts,
          locations,
          all,
        };
      })
      .addCase(fetchTypeAheadSuggestions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // ADDED: saveToRecentSearch cases
      // .addCase(saveToRecentSearch.pending, (state) => {
      //   // Optional: Add loading state for recent search
      //   // state.isLoading = true;
      //   console.log('Saving recent search...', state);
      // })
        // .addCase(saveToRecentSearch.fulfilled, () => {
        //   // Recent search saved successfully
        //   // state.isLoading = false;
        //   // The API call handles localStorage cleanup
        // })
        // .addCase(saveToRecentSearch.rejected, () => {
        //   // Handle recent search save failure (non-critical)
        //   // state.isLoading = false;
        // });
  },
});

export const { 
  setTypeAheadQuery, 
  clearTypeAheadSuggestions, 
  setSelectedItem, 
  clearSelectedItem 
} = typeaheadSlice.actions;

// Selectors
export const selectTypeaheadState = (state: { typeahead: TypeAheadState }) => state.typeahead;
export const selectSelectedItem = (state: { typeahead: TypeAheadState }) => state.typeahead.selectedItem;
export const selectTypeaheadSuggestions = (state: { typeahead: TypeAheadState }) => state.typeahead.suggestions;
export const selectTypeaheadLoading = (state: { typeahead: TypeAheadState }) => state.typeahead.isLoading;
export const selectTypeaheadError = (state: { typeahead: TypeAheadState }) => state.typeahead.error;

// Export the helper function for use in other components
export { makeRecentSearchApiCall };

export default typeaheadSlice.reducer;